import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  Send, Search, Filter, CheckSquare, Square, ChevronDown, ChevronRight,
  FileText, Users, Building2, Mail, Clock, Calendar, AlertTriangle,
  Eye, Loader2, Check, X, Play, Pause, RefreshCw
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BulkProposalSender = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuth();
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1: Template selection
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  
  // Step 2: Recipient selection
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [expandedCompanies, setExpandedCompanies] = useState({});
  
  // Step 3: Personalization
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  
  // Step 4: Review & Send
  const [scheduleType, setScheduleType] = useState('now');
  const [scheduledDate, setScheduledDate] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ current: 0, total: 0, results: [] });
  const [sendComplete, setSendComplete] = useState(false);
  
  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);
  
  // Fetch companies when moving to step 2
  useEffect(() => {
    if (currentStep === 2 && companies.length === 0) {
      fetchCompanies();
    }
  }, [currentStep]);
  
  // Set default subject when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      setSubject(`{{company_name}} - ${selectedTemplate.name}`);
    }
  }, [selectedTemplate]);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/proposal-templates`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      // Fetch all cards from the member tracking board
      // First get the board ID for member_tracking
      const boardsResponse = await fetch(`${BACKEND_URL}/api/boards`, {
        credentials: 'include'
      });
      
      let memberTrackingBoardId = null;
      if (boardsResponse.ok) {
        const boards = await boardsResponse.json();
        const memberBoard = boards.find(b => b.board_type === 'member_tracking');
        if (memberBoard) {
          memberTrackingBoardId = memberBoard.board_id;
        }
      }
      
      if (!memberTrackingBoardId) {
        console.error('Member tracking board not found');
        setLoadingCompanies(false);
        return;
      }
      
      // Fetch cards from the member tracking board
      const response = await fetch(`${BACKEND_URL}/api/boards/${memberTrackingBoardId}/cards`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter to only include companies with at least one contact with email
        const companiesWithContacts = data.filter(card => {
          const contacts = card.key_people || [];
          return contacts.some(c => c.email);
        }).map(card => ({
          ...card,
          company_name: card.company_name || card.title || 'Unknown Company',
          contacts: (card.key_people || []).filter(c => c.email)
        }));
        setCompanies(companiesWithContacts);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Get unique values for filters
  const stages = [...new Set(companies.map(c => c.relationship_status || c.status).filter(Boolean))];
  const industries = [...new Set(companies.map(c => c.industry).filter(Boolean))];

  // Filter companies
  const filteredCompanies = companies.filter(company => {
    const matchesSearch = !searchQuery || 
      company.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.contacts?.some(c => 
        c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
      );
    const companyStage = company.relationship_status || company.status;
    const matchesStage = filterStage === 'all' || companyStage === filterStage;
    const matchesIndustry = filterIndustry === 'all' || company.industry === filterIndustry;
    return matchesSearch && matchesStage && matchesIndustry;
  });

  // Toggle recipient selection
  const toggleRecipient = (company, contact) => {
    const recipientId = `${company.card_id}_${contact.email}`;
    const existing = selectedRecipients.find(r => r.id === recipientId);
    
    if (existing) {
      setSelectedRecipients(selectedRecipients.filter(r => r.id !== recipientId));
    } else {
      setSelectedRecipients([...selectedRecipients, {
        id: recipientId,
        card_id: company.card_id,
        company_name: company.company_name,
        contact_name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
        contact_first_name: contact.first_name || '',
        email: contact.email,
        industry: company.industry || ''
      }]);
    }
  };

  // Select all contacts from a company
  const toggleCompanySelection = (company) => {
    const companyRecipientIds = company.contacts.map(c => `${company.card_id}_${c.email}`);
    const allSelected = companyRecipientIds.every(id => 
      selectedRecipients.some(r => r.id === id)
    );
    
    if (allSelected) {
      // Deselect all from this company
      setSelectedRecipients(selectedRecipients.filter(r => 
        !companyRecipientIds.includes(r.id)
      ));
    } else {
      // Select all from this company
      const newRecipients = company.contacts
        .filter(contact => !selectedRecipients.some(r => r.id === `${company.card_id}_${contact.email}`))
        .map(contact => ({
          id: `${company.card_id}_${contact.email}`,
          card_id: company.card_id,
          company_name: company.company_name,
          contact_name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
          contact_first_name: contact.first_name || '',
          email: contact.email,
          industry: company.industry || ''
        }));
      setSelectedRecipients([...selectedRecipients, ...newRecipients]);
    }
  };

  // Select all visible recipients
  const selectAllVisible = () => {
    const allVisibleRecipients = filteredCompanies.flatMap(company => 
      company.contacts.map(contact => ({
        id: `${company.card_id}_${contact.email}`,
        card_id: company.card_id,
        company_name: company.company_name,
        contact_name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
        contact_first_name: contact.first_name || '',
        email: contact.email,
        industry: company.industry || ''
      }))
    );
    
    const allSelected = allVisibleRecipients.every(r => 
      selectedRecipients.some(sr => sr.id === r.id)
    );
    
    if (allSelected) {
      // Deselect all visible
      const visibleIds = allVisibleRecipients.map(r => r.id);
      setSelectedRecipients(selectedRecipients.filter(r => !visibleIds.includes(r.id)));
    } else {
      // Select all visible (merge with existing)
      const newRecipients = allVisibleRecipients.filter(r => 
        !selectedRecipients.some(sr => sr.id === r.id)
      );
      setSelectedRecipients([...selectedRecipients, ...newRecipients]);
    }
  };

  // Replace merge tags in text
  const replaceMergeTags = (text, recipient) => {
    return text
      .replace(/\{\{company_name\}\}/g, recipient.company_name || '')
      .replace(/\{\{contact_name\}\}/g, recipient.contact_name || '')
      .replace(/\{\{contact_first_name\}\}/g, recipient.contact_first_name || '')
      .replace(/\{\{industry\}\}/g, recipient.industry || '');
  };

  // Send bulk proposals
  const handleSendBulk = async () => {
    if (selectedRecipients.length === 0) {
      alert('Please select at least one recipient');
      return;
    }
    
    setSending(true);
    setSendProgress({ current: 0, total: selectedRecipients.length, results: [] });
    
    const bulkSendId = `bulk_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/bulk-send/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          bulk_send_id: bulkSendId,
          template_id: selectedTemplate.template_id,
          recipients: selectedRecipients,
          subject_template: subject,
          message_template: message,
          schedule_type: scheduleType,
          scheduled_at: scheduleType === 'later' ? scheduledDate?.toISOString() : null
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setSendProgress({
          current: result.total,
          total: result.total,
          results: result.results || []
        });
        setSendComplete(true);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to send bulk proposals');
      }
    } catch (error) {
      console.error('Bulk send error:', error);
      alert('Failed to send bulk proposals');
    } finally {
      setSending(false);
    }
  };

  // Step indicators
  const steps = [
    { num: 1, label: 'Template', icon: FileText },
    { num: 2, label: 'Recipients', icon: Users },
    { num: 3, label: 'Personalize', icon: Mail },
    { num: 4, label: 'Send', icon: Send }
  ];

  const canProceed = () => {
    switch (currentStep) {
      case 1: return selectedTemplate !== null;
      case 2: return selectedRecipients.length > 0;
      case 3: return subject.trim() !== '';
      case 4: return scheduleType === 'now' || (scheduleType === 'later' && scheduledDate);
      default: return false;
    }
  };

  return (
    <AppLayout>
      <div className={`min-h-screen ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="max-w-6xl mx-auto p-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-3`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <Send className="w-7 h-7 text-[#A2FF00]" />
              Bulk Proposal Sender
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Send personalized proposals to multiple recipients at once
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.num;
              const isComplete = currentStep > step.num;
              
              return (
                <React.Fragment key={step.num}>
                  <div 
                    className={`flex items-center gap-2 cursor-pointer ${
                      isActive || isComplete ? '' : 'opacity-50'
                    }`}
                    onClick={() => isComplete && setCurrentStep(step.num)}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isActive 
                        ? 'bg-[#A2FF00] text-black' 
                        : isComplete 
                          ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'
                          : isDark ? 'bg-white/10 text-gray-500' : 'bg-gray-200 text-gray-400'
                    }`}>
                      {isComplete ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className={`text-sm font-medium ${
                      isActive 
                        ? isDark ? 'text-[#A2FF00]' : 'text-emerald-600'
                        : isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-px mx-4 ${
                      currentStep > step.num 
                        ? 'bg-[#A2FF00]' 
                        : isDark ? 'bg-white/10' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Step Content */}
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`}>
            
            {/* Step 1: Select Template */}
            {currentStep === 1 && (
              <div>
                <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>
                  Select Proposal Template
                </h2>
                
                {loadingTemplates ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#A2FF00]" />
                  </div>
                ) : templates.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                    <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>No templates found</p>
                    <button
                      onClick={() => navigate('/proposals/templates')}
                      className="mt-4 text-[#A2FF00] hover:underline"
                    >
                      Create a template first
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {templates.map(template => (
                      <div
                        key={template.template_id}
                        onClick={() => setSelectedTemplate(template)}
                        className={`p-4 rounded-sm border-2 cursor-pointer transition-all ${
                          selectedTemplate?.template_id === template.template_id
                            ? 'border-[#A2FF00] bg-[#A2FF00]/10'
                            : isDark 
                              ? 'border-white/10 hover:border-white/30' 
                              : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {template.name}
                            </h3>
                            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {template.sections?.length || 0} sections
                            </p>
                          </div>
                          {selectedTemplate?.template_id === template.template_id && (
                            <Check className="w-5 h-5 text-[#A2FF00]" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Select Recipients */}
            {currentStep === 2 && (
              <div>
                <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>
                  Select Recipients
                </h2>
                
                {/* Search and Filters */}
                <div className="flex flex-wrap gap-4 mb-4">
                  <div className="flex-1 min-w-[200px] relative">
                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <input
                      type="text"
                      placeholder="Search companies or contacts..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={`w-full pl-10 pr-4 py-2 rounded-sm border ${
                        isDark 
                          ? 'bg-black border-white/20 text-white placeholder-gray-500' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } focus:border-[#A2FF00] outline-none`}
                    />
                  </div>
                  
                  <select
                    value={filterStage}
                    onChange={(e) => setFilterStage(e.target.value)}
                    className={`px-4 py-2 rounded-sm border ${
                      isDark 
                        ? 'bg-black border-white/20 text-white' 
                        : 'bg-white border-gray-200 text-gray-900'
                    } focus:border-[#A2FF00] outline-none`}
                  >
                    <option value="all">All Stages</option>
                    {stages.map(stage => (
                      <option key={stage} value={stage}>{stage}</option>
                    ))}
                  </select>
                  
                  <select
                    value={filterIndustry}
                    onChange={(e) => setFilterIndustry(e.target.value)}
                    className={`px-4 py-2 rounded-sm border ${
                      isDark 
                        ? 'bg-black border-white/20 text-white' 
                        : 'bg-white border-gray-200 text-gray-900'
                    } focus:border-[#A2FF00] outline-none`}
                  >
                    <option value="all">All Industries</option>
                    {industries.map(ind => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>

                {/* Selection Summary */}
                <div className={`flex items-center justify-between p-3 rounded-sm mb-4 ${
                  isDark ? 'bg-black/50' : 'bg-gray-50'
                }`}>
                  <button
                    onClick={selectAllVisible}
                    className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    <CheckSquare className="w-4 h-4" />
                    Select All Visible ({filteredCompanies.reduce((acc, c) => acc + c.contacts.length, 0)})
                  </button>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span className="text-[#A2FF00] font-bold">{selectedRecipients.length}</span> recipients selected
                  </div>
                </div>

                {/* Companies List */}
                {loadingCompanies ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#A2FF00]" />
                  </div>
                ) : filteredCompanies.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                    <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                      No companies with email contacts found
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {filteredCompanies.map(company => {
                      const isExpanded = expandedCompanies[company.card_id];
                      const companyRecipientIds = company.contacts.map(c => `${company.card_id}_${c.email}`);
                      const selectedCount = companyRecipientIds.filter(id => 
                        selectedRecipients.some(r => r.id === id)
                      ).length;
                      const allSelected = selectedCount === company.contacts.length;
                      
                      return (
                        <div 
                          key={company.card_id}
                          className={`border rounded-sm ${isDark ? 'border-white/10' : 'border-gray-200'}`}
                        >
                          {/* Company Header */}
                          <div 
                            className={`flex items-center gap-3 p-3 cursor-pointer ${
                              isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                            }`}
                            onClick={() => setExpandedCompanies({
                              ...expandedCompanies,
                              [company.card_id]: !isExpanded
                            })}
                          >
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleCompanySelection(company); }}
                              className={`p-1 rounded ${allSelected ? 'text-[#A2FF00]' : isDark ? 'text-gray-500' : 'text-gray-400'}`}
                            >
                              {allSelected ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                            </button>
                            
                            {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                            
                            <div className="flex-1">
                              <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {company.company_name}
                              </span>
                              <span className={`ml-2 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                ({company.contacts.length} contacts)
                              </span>
                            </div>
                            
                            {selectedCount > 0 && (
                              <span className="px-2 py-0.5 text-xs bg-[#A2FF00]/20 text-[#A2FF00] rounded">
                                {selectedCount} selected
                              </span>
                            )}
                          </div>
                          
                          {/* Contacts */}
                          {isExpanded && (
                            <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                              {company.contacts.map(contact => {
                                const recipientId = `${company.card_id}_${contact.email}`;
                                const isSelected = selectedRecipients.some(r => r.id === recipientId);
                                
                                return (
                                  <div
                                    key={contact.email}
                                    onClick={() => toggleRecipient(company, contact)}
                                    className={`flex items-center gap-3 p-3 pl-12 cursor-pointer ${
                                      isSelected 
                                        ? isDark ? 'bg-[#A2FF00]/10' : 'bg-emerald-50'
                                        : isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className={isSelected ? 'text-[#A2FF00]' : isDark ? 'text-gray-500' : 'text-gray-400'}>
                                      {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                    </div>
                                    <div className="flex-1">
                                      <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                                        {contact.first_name} {contact.last_name}
                                      </span>
                                      <span className={`ml-2 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {contact.email}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Personalization */}
            {currentStep === 3 && (
              <div>
                <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>
                  Personalize Your Message
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Email Subject
                    </label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="e.g., {{company_name}} - Exclusive Membership Proposal"
                      className={`w-full px-4 py-2 rounded-sm border ${
                        isDark 
                          ? 'bg-black border-white/20 text-white placeholder-gray-500' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } focus:border-[#A2FF00] outline-none`}
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Email Message
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={6}
                      placeholder={`Dear {{contact_first_name}},\n\nI'm pleased to share this exclusive proposal for {{company_name}}...\n\nBest regards`}
                      className={`w-full px-4 py-2 rounded-sm border ${
                        isDark 
                          ? 'bg-black border-white/20 text-white placeholder-gray-500' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } focus:border-[#A2FF00] outline-none`}
                    />
                  </div>
                  
                  {/* Merge Tags Legend */}
                  <div className={`p-4 rounded-sm border ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                    <h4 className={`text-sm font-medium mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <span className="text-[#A2FF00]">{ }</span> Available Merge Tags
                    </h4>
                    <p className={`text-xs mb-3 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      Click on a tag to copy it, then paste into your subject or message.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {[
                        { tag: '{{company_name}}', desc: 'Company name', example: 'Acme Corp' },
                        { tag: '{{contact_name}}', desc: 'Full name', example: 'John Smith' },
                        { tag: '{{contact_first_name}}', desc: 'First name', example: 'John' },
                        { tag: '{{industry}}', desc: 'Industry', example: 'Technology' },
                      ].map(item => (
                        <button
                          key={item.tag}
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(item.tag);
                            // Brief visual feedback could be added here
                          }}
                          className={`p-2 rounded-sm border text-left transition-all hover:border-[#A2FF00] hover:bg-[#A2FF00]/5 ${
                            isDark ? 'border-white/10 bg-black/50' : 'border-gray-200 bg-white'
                          }`}
                          title={`Click to copy: ${item.tag}`}
                        >
                          <code className={`text-xs font-mono block mb-1 ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'}`}>
                            {item.tag}
                          </code>
                          <span className={`text-xs block ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {item.desc}
                          </span>
                          <span className={`text-xs block italic ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                            e.g., "{item.example}"
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Preview */}
                  {selectedRecipients.length > 0 && (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Preview (for {selectedRecipients[0].contact_name || selectedRecipients[0].email})
                      </label>
                      <div className={`p-4 rounded-sm border ${isDark ? 'bg-black/50 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                        <p className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Subject: {replaceMergeTags(subject, selectedRecipients[0])}
                        </p>
                        <p className={`whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {replaceMergeTags(message, selectedRecipients[0]) || '(No message)'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: Review & Send */}
            {currentStep === 4 && !sendComplete && (
              <div>
                <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>
                  Review & Send
                </h2>
                
                {/* Summary */}
                <div className={`p-4 rounded-sm border mb-6 ${isDark ? 'bg-black/50 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                  <h3 className={`font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Template:</span>
                      <span className={isDark ? 'text-white' : 'text-gray-900'}>{selectedTemplate?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Recipients:</span>
                      <span className={isDark ? 'text-white' : 'text-gray-900'}>{selectedRecipients.length} contacts</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Proposals to create:</span>
                      <span className={isDark ? 'text-white' : 'text-gray-900'}>{selectedRecipients.length}</span>
                    </div>
                  </div>
                </div>
                
                {/* Schedule Options */}
                <div className="mb-6">
                  <h3 className={`font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>When to send?</h3>
                  <div className="space-y-3">
                    <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer ${
                      scheduleType === 'now'
                        ? 'border-[#A2FF00] bg-[#A2FF00]/10'
                        : isDark ? 'border-white/10 hover:border-white/30' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="schedule"
                        checked={scheduleType === 'now'}
                        onChange={() => setScheduleType('now')}
                        className="text-[#A2FF00]"
                      />
                      <Play className="w-5 h-5 text-[#A2FF00]" />
                      <div>
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Send Now</span>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Start sending immediately
                        </p>
                      </div>
                    </label>
                    
                    <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer ${
                      scheduleType === 'later'
                        ? 'border-[#A2FF00] bg-[#A2FF00]/10'
                        : isDark ? 'border-white/10 hover:border-white/30' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="schedule"
                        checked={scheduleType === 'later'}
                        onChange={() => setScheduleType('later')}
                        className="text-[#A2FF00]"
                      />
                      <Clock className="w-5 h-5 text-blue-400" />
                      <div className="flex-1">
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Schedule for Later</span>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Choose a specific date and time
                        </p>
                      </div>
                    </label>
                    
                    {scheduleType === 'later' && (
                      <div className="ml-12">
                        <DatePicker
                          selected={scheduledDate}
                          onChange={(date) => setScheduledDate(date)}
                          showTimeSelect
                          minDate={new Date()}
                          dateFormat="MMMM d, yyyy h:mm aa"
                          className={`w-full px-4 py-2 rounded-sm border ${
                            isDark 
                              ? 'bg-black border-white/20 text-white' 
                              : 'bg-white border-gray-200 text-gray-900'
                          } focus:border-[#A2FF00] outline-none`}
                          placeholderText="Select date and time"
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Warning */}
                <div className={`p-3 rounded-sm border mb-6 ${isDark ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                    <p className={`text-sm ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>
                      This will create {selectedRecipients.length} individual proposals and send emails via Brevo.
                      Each recipient will receive a unique proposal link.
                    </p>
                  </div>
                </div>
                
                {/* Send Button */}
                <button
                  onClick={handleSendBulk}
                  disabled={sending || !canProceed()}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#A2FF00] hover:bg-[#8FE600] text-black font-bold rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending {sendProgress.current} of {sendProgress.total}...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {scheduleType === 'now' ? `Send ${selectedRecipients.length} Proposals` : 'Schedule Send'}
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Send Complete */}
            {sendComplete && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-400" />
                </div>
                <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {scheduleType === 'now' ? 'Proposals Sent!' : 'Proposals Scheduled!'}
                </h2>
                <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Successfully processed {sendProgress.total} proposals
                </p>
                
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => navigate('/proposals')}
                    className="px-6 py-2 bg-[#A2FF00] hover:bg-[#8FE600] text-black font-bold rounded-sm transition-colors"
                  >
                    View Proposals
                  </button>
                  <button
                    onClick={() => {
                      setCurrentStep(1);
                      setSelectedTemplate(null);
                      setSelectedRecipients([]);
                      setSubject('');
                      setMessage('');
                      setSendComplete(false);
                      setSendProgress({ current: 0, total: 0, results: [] });
                    }}
                    className={`px-6 py-2 border rounded-sm transition-colors ${
                      isDark 
                        ? 'border-white/20 text-white hover:bg-white/10' 
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Send Another Batch
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          {!sendComplete && (
            <div className="flex justify-between mt-6">
              <button
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
                className={`px-6 py-2 rounded-sm transition-colors ${
                  currentStep === 1
                    ? 'opacity-50 cursor-not-allowed'
                    : isDark 
                      ? 'text-gray-400 hover:text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Back
              </button>
              
              {currentStep < 4 && (
                <button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={!canProceed()}
                  className="px-6 py-2 bg-[#A2FF00] hover:bg-[#8FE600] text-black font-bold rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default BulkProposalSender;
